import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { CinematicBackground } from '@/components/CinematicBackground';
import { GlassCard } from '@/components/GlassCard';
import { Brand } from '@/components/Brand';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Sparkles, Calendar, KanbanSquare, Wallet, Shield, Users, Check, Download, Star, ArrowRight,
} from 'lucide-react';

const ICONS = { calendar: Calendar, kanban: KanbanSquare, wallet: Wallet, sparkles: Sparkles, shield: Shield, users: Users };

export default function Landing() {
  const navigate = useNavigate();
  const [cms, setCms] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    // El landing SIEMPRE es oscuro, independiente del tema de la app (scope local .dark)
    api.get('/cms/landing').then(({ data }) => setCms(data)).catch(() => {});
    api.get('/platform/public-tiers').then(({ data }) => setTiers(data)).catch(() => {});
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  const install = async () => {
    if (installEvt) { installEvt.prompt(); await installEvt.userChoice; setInstallEvt(null); }
    else navigate('/register');
  };

  if (!cms) return <div className="min-h-screen bg-[#07090A]" />;
  const { hero, features, pricing, security, testimonials, faq, footer, announcement } = cms;

  return (
    <div className="dark relative min-h-screen text-white overflow-x-hidden" style={{ background: '#07090A' }}>
      <CinematicBackground />

      {announcement?.enabled && announcement?.text && (
        <div className="relative z-10 text-center text-sm py-2 px-4" style={{ background: 'rgba(201,168,76,0.15)' }} data-testid="landing-announcement">{announcement.text}</div>
      )}

      {/* Nav */}
      <header className="relative z-20 flex items-center justify-between px-5 sm:px-10 py-5 max-w-7xl mx-auto">
        <Brand size={38} />
        <div className="flex items-center gap-3">
          <button data-testid="landing-login-btn" onClick={() => navigate('/login')} className="text-sm text-[#A6C3B4] hover:text-white">Iniciar sesión</button>
          <button data-testid="landing-register-btn" onClick={() => navigate('/register')} className="text-sm px-4 h-9 rounded-xl font-medium text-[#07090A]" style={{ background: '#3EB489' }}>Empezar gratis</button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-xs uppercase tracking-[0.22em] pp-gold mb-4">{hero.tagline}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight pp-text-glow">{hero.title}</h1>
          <p className="text-base sm:text-lg text-[#A6C3B4] mt-6 max-w-xl">{hero.subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button data-testid="pwa-install-button" onClick={install} className="h-13 px-6 py-3.5 rounded-2xl font-semibold text-[#07090A] flex items-center gap-2 pp-glow-mint" style={{ background: '#3EB489' }}>
              <Download size={18} /> {hero.cta_primary}
            </button>
            <button data-testid="hero-demo-btn" onClick={() => navigate('/login')} className="h-13 px-6 py-3.5 rounded-2xl font-semibold border border-[rgba(168,230,206,0.25)] flex items-center gap-2 hover:bg-[rgba(255,255,255,0.05)]">
              {hero.cta_secondary} <ArrowRight size={18} />
            </button>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.94, rotateY: 8 }} animate={{ opacity: 1, scale: 1, rotateY: 0 }} transition={{ duration: 0.8 }}>
          <GlassCard tilt className="p-3" testid="hero-preview">
            <img src="/brand/ad-control.jpg" alt="PlannerPRO en acción" className="rounded-2xl w-full object-cover aspect-[3/4] sm:aspect-[4/3]" />
          </GlassCard>
        </motion.div>
      </section>

      {/* Features */}
      {features?.enabled && (
        <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 py-16">
          <h2 className="text-3xl font-bold text-center mb-3">Todo tu mundo, en un cockpit</h2>
          <p className="text-center text-[#A6C3B4] mb-12 max-w-2xl mx-auto">Módulos poderosos con estética cinematográfica y IA integrada.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.items.map((f, i) => {
              const Icon = ICONS[f.icon] || Sparkles;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <GlassCard className="p-6 h-full" testid={`feature-${i}`}>
                    <div className="w-12 h-12 rounded-2xl grid place-items-center mb-4 pp-glow-mint" style={{ background: 'rgba(62,180,137,0.12)' }}><Icon className="pp-mint" size={24} /></div>
                    <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                    <p className="text-sm text-[#A6C3B4]">{f.desc}</p>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* Demo Visual */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-10 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <div className="text-xs uppercase tracking-[0.2em] pp-gold mb-3">El control en tus manos</div>
          <h2 className="text-3xl font-bold mb-4">Tu agenda, finanzas y equipo — en cualquier lugar</h2>
          <p className="text-[#A6C3B4] mb-6">Instálalo como app nativa en tu escritorio o móvil. Todo sincronizado, cifrado y con la estética cinematográfica que mereces.</p>
          <button onClick={install} className="h-12 px-6 rounded-2xl font-semibold text-[#07090A] inline-flex items-center gap-2" style={{ background: '#3EB489' }}><Download size={18} /> {hero.cta_primary}</button>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="flex justify-center">
          <img src="/brand/mock-mobile.jpg" alt="PlannerPRO móvil" className="rounded-3xl max-h-[520px] object-contain pp-glow-mint" />
        </motion.div>
      </section>

      {/* Pricing (dinámico desde Super Master, con fallback al CMS) */}
      {pricing?.enabled && (
        <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 py-16" id="planes">
          <h2 className="text-3xl font-bold text-center mb-12">Planes para cada etapa</h2>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {(tiers.length ? tiers : pricing.items).map((p, i) => (
              <GlassCard key={p.key || i} gold={p.highlight} className={`p-7 ${p.highlight ? 'pp-glow-gold scale-[1.03]' : ''}`} testid={`plan-${i}`}>
                {p.highlight && <div className="text-xs pp-gold uppercase tracking-wide mb-2">Más popular</div>}
                <div className="font-semibold text-lg">{p.name}</div>
                <div className="mt-3 mb-5"><span className="text-4xl font-black">{p.price}</span><span className="text-[#A6C3B4]">{p.period}</span></div>
                <ul className="space-y-2 mb-6">
                  {(p.features || []).map((ft, j) => <li key={j} className="flex items-center gap-2 text-sm text-[#A6C3B4]"><Check size={15} className="pp-mint" /> {ft}</li>)}
                </ul>
                <button onClick={() => navigate('/register')} className={`w-full h-11 rounded-xl font-semibold ${p.highlight ? 'text-[#07090A]' : 'border border-[rgba(168,230,206,0.25)]'}`} style={p.highlight ? { background: '#C9A84C' } : {}}>Elegir {p.name}</button>
              </GlassCard>
            ))}
          </div>
        </section>
      )}

      {/* Security */}
      {security?.enabled && (
        <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-10 py-16">
          <GlassCard className="p-10 text-center" testid="security-section">
            <Shield className="pp-mint mx-auto mb-4" size={40} />
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">{security.title}</h2>
            <p className="text-[#A6C3B4] max-w-2xl mx-auto mb-6">{security.body}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {security.badges?.map((b) => <span key={b} className="text-xs px-4 py-1.5 rounded-full border border-[rgba(168,230,206,0.25)] pp-mint">{b}</span>)}
            </div>
          </GlassCard>
        </section>
      )}

      {/* Testimonials */}
      {testimonials?.enabled && (
        <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 py-16">
          <h2 className="text-3xl font-bold text-center mb-12">Lo que dicen nuestros usuarios</h2>
          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {testimonials.items.map((t, i) => (
              <GlassCard key={i} className="p-7" testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-3">{[...Array(5)].map((_, j) => <Star key={j} size={15} className="pp-gold fill-current" />)}</div>
                <p className="text-[#dfeee7] italic mb-4">"{t.text}"</p>
                <div className="font-semibold">{t.name}</div>
                <div className="text-xs text-[#A6C3B4]">{t.role}</div>
              </GlassCard>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq?.enabled && (
        <section className="relative z-10 max-w-3xl mx-auto px-5 sm:px-10 py-16">
          <h2 className="text-3xl font-bold text-center mb-10">Preguntas frecuentes</h2>
          <GlassCard className="p-4 sm:p-8" testid="faq-section">
            <Accordion type="single" collapsible>
              {faq.items.map((f, i) => (
                <AccordionItem key={i} value={`i${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#A6C3B4]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </GlassCard>
        </section>
      )}

      {/* CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-10 py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-black mb-4 pp-text-glow">Da el paso a la era de los dashboards del futuro</h2>
        <button data-testid="cta-install" onClick={install} className="h-13 px-8 py-3.5 rounded-2xl font-semibold text-[#07090A] inline-flex items-center gap-2 pp-glow-mint" style={{ background: '#3EB489' }}>
          <Download size={18} /> {hero.cta_primary}
        </button>
      </section>

      {/* Secciones personalizadas creadas desde el Super Master */}
      {(cms?.custom_sections || []).filter((s) => s.enabled !== false).map((s) => (
        <section key={s.id} className="relative z-10 max-w-5xl mx-auto px-5 sm:px-10 py-16" data-testid={`landing-custom-${s.id}`}>
          <h2 className="text-3xl font-bold text-center mb-3">{s.title}</h2>
          {s.subtitle && <p className="text-center text-[#A6C3B4] mb-6 max-w-2xl mx-auto">{s.subtitle}</p>}
          {s.body && (
            <GlassCard className="p-8">
              <p className="text-[#dfeee7] whitespace-pre-line leading-relaxed">{s.body}</p>
            </GlassCard>
          )}
        </section>
      ))}

      <footer className="relative z-10 border-t border-[rgba(168,230,206,0.12)] py-8 px-5 sm:px-10">
        <div className="max-w-7xl mx-auto flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3"><Brand size={30} /><span className="text-xs text-[#6b8479]">www.plannerpro.win</span></div>
            <div className="flex gap-5">{footer?.links?.map((l) => <span key={l} className="text-sm text-[#A6C3B4] hover:text-white cursor-pointer">{l}</span>)}</div>
          </div>
          <div className="pt-4 border-t border-[rgba(168,230,206,0.08)] flex flex-col sm:flex-row items-center justify-between gap-2 text-center">
            <span className="text-xs text-[#6b8479]">{footer?.text || '© 2026 PlannerPRO. Todos los derechos reservados.'}</span>
            <span className="text-xs text-[#6b8479]">Desarrollado por <a href="https://www.7-study.com" target="_blank" rel="noreferrer" className="pp-mint font-semibold hover:underline">7-study</a> · <a href="https://www.7-study.com" target="_blank" rel="noreferrer" className="hover:text-white">www.7-study.com</a> · Derechos reservados</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
